import { Fragment, useState, memo, MouseEvent } from 'react'
import { Table } from 'semantic-ui-react'
import { isArray } from 'lodash-es'
import Color from 'color'

import { useTheme } from '@/hooks'
import { FlexRow, Icon, Form, Text } from '@/ui'
import { renderTitle } from '..'
import { TableRow, MobileCell, MobileSpan } from './Styles'
import { TFunction } from 'i18next'

interface CreateCellsProps {
  d: any
  columns: any[]
  checked: string[]
  clickable: (d: any) => void
  childrenVisible?: boolean
  onToggleChildren?: (e: MouseEvent<HTMLButtonElement>) => void
  onCheck: (id: string | string[]) => void
  isMobile: boolean
  theme: any
  t: TFunction
}
interface TableRowProps {
  clickable: (d: any) => void
  columns: any[]
  onCheck: (id: string | string[]) => void
  onCheckAll: (e: MouseEvent<HTMLButtonElement>) => void
  checked: string[]
  loading: boolean
  search: string
  t: TFunction
  isMobile: boolean
  fallbackText: string
  data: any[]
}
interface StyledTableRowProps {
  children?: React.ReactNode
  style?: React.CSSProperties
  textAlign?: 'left' | 'center' | 'right'
  columns?: any
  checked?: any
  onCheck?: any
  onCheckAll?: any
  isMobile?: any
  t?: TFunction
  d?: any
  clickable?: any
}

const createCells = ({
  d,
  columns,
  checked,
  clickable,
  childrenVisible,
  onToggleChildren,
  onCheck,
  isMobile,
  t,
}: CreateCellsProps) => {
  let skipRemainingCells = false

  return columns
    .map(c => {
      let cell

      if (skipRemainingCells) {
        return null
      }

      if (c.checkbox) {
        if (isMobile) {
          return <div key={c.key} />
        }

        cell = (
          <Form.Checkbox
            checked={checked && checked.includes(d[c.key])}
            onChange={() => {
              if (d.children) {
                const dataValues = [
                  d[c.key],
                  ...(d?.children ?? []).map((child: any) => child[c.key]),
                ]

                const isAllChecked = dataValues.every(x => checked.includes(x))

                return onCheck(
                  isAllChecked
                    ? dataValues
                    : dataValues.filter(x => !checked.includes(x)),
                )
              }

              // uncheck parent if not all its children are checked
              if (
                checked.includes(d[c.key]) &&
                checked.includes(d?.untouchedParentNode?.id)
              ) {
                onCheck(d.untouchedParentNode.id)
              }

              onCheck(d[c.key])
            }}
          />
        )
      }

      if (d.Component) {
        cell = d.Component
      }

      if (!cell) {
        cell =
          d[c.key] &&
          typeof d[c.key] === 'object' &&
          Object.prototype.hasOwnProperty.call(d[c.key], 'Component')
            ? d[c.key].Component
            : d[c.key]
      }

      const cellProps: any = {}
      if (!c.checkbox && c.key !== 'options' && clickable && c.clickable) {
        cellProps.onClick = () => clickable(d)
      }
      if (c.textAlign) {
        cellProps.textAlign = c.textAlign
      }
      if (c.verticalAlign) {
        cellProps.verticalAlign = c.verticalAlign
      }
      if (d.Component) {
        skipRemainingCells = true
        cellProps.colSpan = columns.length
      }
      const title = renderTitle(c.title, t)

      if (!isMobile) {
        if (isArray(d.children) && c.key === 'name') {
          return (
            <Table.Cell {...cellProps} key={c.key}>
              <FlexRow noMargin>
                {cell}

                {!!d.children.length && (
                  <Icon.Toggle
                    size="big"
                    toggled={childrenVisible}
                    onClick={onToggleChildren}
                  />
                )}
              </FlexRow>
            </Table.Cell>
          )
        }

        return (
          <Table.Cell {...cellProps} key={c.key}>
            {cell}
          </Table.Cell>
        )
      }

      return (
        <Table.Cell {...cellProps} key={c.key}>
          <MobileCell>
            <MobileSpan>
              {
                <MobileSpan>
                  {c.key === 'options' ? t('Options') : title}
                </MobileSpan>
              }
            </MobileSpan>

            <FlexRow noMargin justifyContent="flex-end">
              {cell ? (
                <MobileSpan light>{cell}</MobileSpan>
              ) : (
                <MobileSpan light grey>
                  {t('No value')}
                </MobileSpan>
              )}
            </FlexRow>
          </MobileCell>
        </Table.Cell>
      )
    })
    .filter(x => x)
}

const TableRows = ({
  clickable,
  columns,
  onCheck,
  onCheckAll,
  checked,
  loading,
  search,
  t,
  isMobile,
  fallbackText,
  data = [],
}: TableRowProps) => {
  if (!data.length) {
    let text = ''

    if (loading) {
      text = `${t('Loading')}...`
    } else if (fallbackText) {
      text = fallbackText
    } else if (search.length) {
      text = t('Your search yielded no results')
    } else {
      text = t('No results')
    }

    return (
      <StyledTableRow textAlign="center">
        <Table.Cell colSpan={columns.length}>
          <Text.Span light>{text}</Text.Span>
        </Table.Cell>
      </StyledTableRow>
    )
  }

  return data.map(d => {
    const rowProps = {
      d: d ?? {},
      clickable,
      columns,
      checked,
      onCheck,
      onCheckAll,
      isMobile,
      t,
    }

    if (d.tableRowStyle) {
      rowProps['style'] = d.tableRowStyle
    }

    return <StyledTableRow key={d.id || Math.random()} {...rowProps} />
  })
}

export const StyledTableRow = ({
  children,
  style,
  textAlign,
  ...props
}: StyledTableRowProps) => {
  const theme = useTheme()
  const [childrenVisible, toggleChildren] = useState(false)

  if (children) {
    return (
      <Table.Row style={style} textAlign={textAlign}>
        {children}
      </Table.Row>
    )
  }

  const cells = props.columns
    ? createCells({
        onToggleChildren: e => {
          e.stopPropagation()
          toggleChildren(!childrenVisible)
        },
        clickable: props.clickable,
        d: props.d,
        columns: props.columns,
        checked: props.checked,
        onCheck: props.onCheck,
        isMobile: props.isMobile,
        t: props.t as TFunction,
        childrenVisible,
        theme,
      })
    : []

  const newStyle = props?.d?.children
    ? {
        backgroundColor: Color(theme.primaryColor).fade(0.9).string(),
        color: 'initial',
      }
    : props?.d?.style

  return (
    <Fragment>
      <TableRow clickable={props.clickable} d={props.d} style={newStyle}>
        {cells}
      </TableRow>

      {childrenVisible &&
        (props?.d?.children ?? []).map((child: any) => (
          <TableRow
            {...props}
            clickable={props.clickable}
            key={child.id}
            d={child}>
            {createCells({
              d: child,
              clickable: props.clickable,
              columns: props.columns,
              checked: props.checked,
              onCheck: props.onCheck,
              isMobile: props.isMobile,
              t: props.t as TFunction,
              theme,
            })}
          </TableRow>
        ))}
    </Fragment>
  )
}

// @ts-ignore
export default memo(TableRows)
